/* __next_internal_client_entry_do_not_use__ Default,NewsArticle auto */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { tv } from 'tailwind-variants';
import { withStandardComponentWrapper } from 'helpers/HOC';
import ImageWrapper from 'helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import RichTextWrapper from 'helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import { parseStyleParams } from 'lib/utils/style-param-utils';
import { getCtaStyle } from 'lib/utils/cta-utils';
import { useCardListContext } from './CardList';
import { getTestProps } from 'lib/testing/utils';
import { createCTAClickGTMEvent } from 'lib/utils/gtm-utils';
import useDictionary from 'lib/hooks/useDictionary';
import { editingHydrationProps } from 'lib/hooks/useEditingHydrationProps';
import useIsEditing from 'lib/hooks/useIsEditing';
const CardItem = (props)=>{
    const isEditing = useIsEditing();
    const { cardImage, cardLink1, cardLink2, description, eyebrow, heading, subHeading } = props?.fields || {};
    const { RenderingIdentifier } = props?.params || {};
    const styles = parseStyleParams(props.params, [
        'cta1',
        'cta2'
    ]);
    // Get column count from CardList context
    const cardListColCount = useCardListContext();
    const { container, header, body, contentWrapper, content, headingWithEyebrow, eyebrow: eyebrowTV, heading: headingTV, subheading: subheadingTV, description: descriptionTV, sectionDivider, footer, cta, imageWrapper } = TAILWIND_VARIANTS({
        colCount: cardListColCount
    });
    return /*#__PURE__*/ _jsxs("article", {
        className: container(),
        "data-component": "authorable/shared/lists/carditem",
        ...editingHydrationProps(isEditing),
        id: RenderingIdentifier,
        ...getTestProps(`card-item-${props?.rendering?.uid}`),
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: header(),
                children: cardImage?.value?.src ? /*#__PURE__*/ _jsx(ImageWrapper, {
                    field: cardImage,
                    className: imageWrapper(),
                    ...getTestProps(`image`)
                }) : /*#__PURE__*/ _jsx(ImageWrapper, {
                    field: {
                        value: {
                            src: '/assets/profile_placeholder.png',
                            alt: 'No image'
                        }
                    },
                    className: imageWrapper(),
                    ...getTestProps(`related-profile-image-placeholder`)
                })
            }),
            /*#__PURE__*/ _jsx("div", {
                className: body(),
                children: /*#__PURE__*/ _jsxs("div", {
                    className: contentWrapper(),
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: content(),
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: headingWithEyebrow(),
                                    children: [
                                        /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                            className: eyebrowTV(),
                                            editable: true,
                                            field: eyebrow,
                                            tag: "div",
                                            ...getTestProps(`eyebrow`)
                                        }),
                                        /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                            className: headingTV(),
                                            field: heading,
                                            ...getTestProps(`heading`)
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                    className: subheadingTV(),
                                    field: subHeading,
                                    ...getTestProps(`sub-heading`)
                                }),
                                /*#__PURE__*/ _jsx(RichTextWrapper, {
                                    className: descriptionTV(),
                                    field: description,
                                    ...getTestProps(`description`)
                                }),
                                cardLink1?.value?.href || cardLink2?.value?.href || cardLink1?.value?.anchor || cardLink2?.value?.anchor ? /*#__PURE__*/ _jsx("span", {
                                    className: sectionDivider()
                                }) : null
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: footer(),
                            children: [
                                /*#__PURE__*/ _jsx(LinkWrapper, {
                                    ctaComponentClass: "component-card-item-button-1-color",
                                    className: cta(),
                                    ctaStyle: getCtaStyle(styles.cta1, 'primary'),
                                    field: cardLink1,
                                    ctaIcon: "arrow-right",
                                    ...getTestProps(`primary-cta`),
                                    gtmEvent: createCTAClickGTMEvent({
                                        header: heading?.value,
                                        ctaText: cardLink1?.value?.text,
                                        ctaType: 'blue',
                                        componentName: 'Card Set',
                                        ctaLink: cardLink1?.value?.href
                                    })
                                }),
                                /*#__PURE__*/ _jsx(LinkWrapper, {
                                    ctaComponentClass: "component-card-item-button-2-color",
                                    className: cta(),
                                    ctaStyle: getCtaStyle(styles.cta2, 'secondary'),
                                    field: cardLink2,
                                    ctaIcon: "arrow-right",
                                    ...getTestProps(`secondary-cta`),
                                    gtmEvent: createCTAClickGTMEvent({
                                        header: heading?.value,
                                        ctaText: cardLink2?.value?.text,
                                        ctaType: 'clear',
                                        componentName: 'Card Set',
                                        ctaLink: cardLink2?.value?.href
                                    })
                                })
                            ]
                        })
                    ]
                })
            })
        ]
    });
};
const NewsArticleItem = (props)=>{
    const isEditing = useIsEditing();
    const { category, newsItem } = props?.fields || {};
    const newsFields = newsItem?.fields;
    const categoryValue = category?.fields?.Value;
    const { RenderingIdentifier } = props?.params || {};
    const { getDictionaryValue } = useDictionary();
    const styles = parseStyleParams(props.params, [
        'cta1',
        'cta2'
    ]);
    // Get column count from CardList context
    const cardListColCount = useCardListContext();
    // Create link field from newsItem.url with dictionary text
    const readMoreText = getDictionaryValue('ReadMore') || 'Read more';
    const newsItemLink = newsItem?.url ? {
        value: {
            href: newsItem.url,
            text: readMoreText,
            linktype: 'internal'
        }
    } : undefined;
    const imageField = newsFields?.image?.value?.src ? newsFields.image : newsFields?.OpenGraphImageUrl?.value?.src ? newsFields.OpenGraphImageUrl : null;
    const { container, header, body, contentWrapper, content, headingWithEyebrow, eyebrowCategory: eyebrowCategoryTV, heading: headingTV, shortDescription: shortDescriptionTV, sectionDivider, footer, cta, imageWrapper } = TAILWIND_VARIANTS({
        colCount: cardListColCount
    });
    return /*#__PURE__*/ _jsxs("article", {
        className: container(),
        "data-component": "authorable/shared/lists/carditem",
        ...editingHydrationProps(isEditing),
        id: RenderingIdentifier,
        ...getTestProps(`card-item-${props?.rendering?.uid}`),
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: header(),
                children: imageField ? /*#__PURE__*/ _jsx(ImageWrapper, {
                    field: imageField,
                    className: imageWrapper(),
                    ...getTestProps('image')
                }) : /*#__PURE__*/ _jsx(ImageWrapper, {
                    field: {
                        value: {
                            src: '/assets/profile_placeholder.png',
                            alt: 'No image'
                        }
                    },
                    className: imageWrapper(),
                    ...getTestProps(`related-profile-image-placeholder`)
                })
            }),
            /*#__PURE__*/ _jsx("div", {
                className: body(),
                children: /*#__PURE__*/ _jsxs("div", {
                    className: contentWrapper(),
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: content(),
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: headingWithEyebrow(),
                                    children: [
                                        /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                            className: eyebrowCategoryTV(),
                                            editable: false,
                                            field: categoryValue,
                                            tag: "div",
                                            ...getTestProps(`category`)
                                        }),
                                        /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                            className: headingTV(),
                                            field: newsFields?.pageTitle,
                                            ...getTestProps(`heading`)
                                        })
                                    ]
                                }),
                                newsFields?.shortDescription ? /*#__PURE__*/ _jsx(RichTextWrapper, {
                                    className: shortDescriptionTV(),
                                    field: newsFields?.shortDescription,
                                    ...getTestProps(`short-description`)
                                }) : /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                    className: shortDescriptionTV(),
                                    field: newsFields?.OpenGraphDescription,
                                    ...getTestProps(`short-description`)
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    className: sectionDivider()
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: footer(),
                            children: newsItemLink && /*#__PURE__*/ _jsx(LinkWrapper, {
                                ctaComponentClass: "component-card-item-button-1-color",
                                className: cta(),
                                ctaStyle: getCtaStyle(styles.cta1, 'primary'),
                                field: newsItemLink,
                                ctaIcon: "arrow-right",
                                ...getTestProps(`primary-cta`),
                                gtmEvent: createCTAClickGTMEvent({
                                    header: newsFields?.pageTitle?.value != null ? String(newsFields.pageTitle.value) : undefined,
                                    ctaText: readMoreText,
                                    ctaType: 'blue',
                                    componentName: 'Card Set',
                                    ctaLink: newsItemLink?.value?.href
                                })
                            })
                        })
                    ]
                })
            })
        ]
    });
};
export const Default = withStandardComponentWrapper(CardItem);
export const NewsArticle = withStandardComponentWrapper(NewsArticleItem);
const TAILWIND_VARIANTS = tv({
    defaultVariants: {
        style: 'primary',
        colCount: '1'
    },
    slots: {
        container: [
            'flex',
            'w-full',
            'overflow-hidden',
            'rounded-general-border-radius-card',
            'bg-component-card-item-surface',
            'shadow-color-black-alpha-10',
            'shadow-[0_0_8px_0]'
        ],
        header: [
            'relative',
            'min-h-max',
            'w-full',
            'h-full md:h-auto',
            'overflow-hidden',
            'aspect-[1.61/1]',
            'text-color-fill-brand-1',
            'transition',
            'duration-300',
            'ease-in-out',
            'group',
            'pt-component-article-card-portrait-image-padding-top',
            'px-component-article-card-portrait-image-padding-x',
            'pb-component-article-card-portrait-image-padding-bottom'
        ],
        body: [
            'flex',
            'flex-col',
            'w-full',
            'items-center'
        ],
        contentWrapper: [
            'flex',
            'flex-col',
            'w-full',
            'py-component-card-item-landscape-content-1-col-padding-y',
            'px-component-card-item-landscape-content-1-col-padding-x'
        ],
        content: [
            'flex',
            'flex-col',
            'w-full',
            'gap-spacing-spacing-24'
        ],
        headingWithEyebrow: [
            'flex',
            'flex-col',
            'w-full',
            'gap-spacing-spacing-4'
        ],
        eyebrow: [
            'text-component-card-item-eyebrow',
            'font-typography-font-family-body',
            'text-typography-font-size-body-xSmall',
            'mb-spacing-spacing-4',
            'font-semibold',
            'font-normal',
            'leading-[150%]'
        ],
        eyebrowCategory: [
            'text-component-card-item-eyebrow',
            'font-typography-font-family-body',
            'text-typography-font-size-body-xSmall',
            'mb-spacing-spacing-4',
            'font-semibold',
            'font-normal',
            'leading-[150%]',
            'uppercase'
        ],
        heading: [
            'font-typography-font-family-header',
            'font-normal',
            'text-typography-font-size-medium',
            'text-component-card-item-title',
            'leading-typography-line-height-medium'
        ],
        subheading: [
            'font-semibold',
            'font-typography-font-family-body',
            'text-typography-font-size-body-small',
            'leading-[150%]',
            'text-component-card-item-subtitle'
        ],
        description: [
            'font-typography-font-family-body',
            'font-typography-font-weight-body',
            'text-typography-font-size-body-medium',
            'text-component-card-item-body',
            'leading-typography-line-height-body-medium',
            'mb-spacing-spacing-8'
        ],
        shortDescription: [
            'font-typography-font-family-body',
            'font-typography-font-weight-body',
            'text-typography-font-size-body-medium',
            'text-component-card-item-body',
            'leading-typography-line-height-body-medium'
        ],
        sectionDivider: [
            'w-component-section-divider-width',
            'h-component-section-divider-height',
            'bg-component-section-divider'
        ],
        footer: [
            'flex',
            'flex-wrap',
            'flex-col',
            'gap-spacing-spacing-8',
            'items-start',
            'justify-center',
            'w-full'
        ],
        cta: [
            'flex',
            'gap-spacing-spacing-8',
            'items-center',
            'justify-center',
            'py-spacing-spacing-16',
            'px-spacing-spacing-24',
            '!w-auto'
        ],
        imageWrapper: [
            'w-full',
            'h-full',
            'object-cover'
        ]
    },
    variants: {
        colCount: {
            '1': {
                container: [
                    'h-full',
                    'flex-col',
                    'md:flex-row',
                    'md:gap-0'
                ],
                body: [
                    'flex-row',
                    'items-start',
                    'text-left',
                    'lg:pl-0',
                    'justify-center'
                ],
                description: [],
                content: [
                    'text-left',
                    'justify-center'
                ],
                contentWrapper: [
                    'text-left',
                    'justify-center',
                    'gap-spacing-spacing-24'
                ],
                header: [
                    'sm:py-component-article-card-landscape-image-padding-y',
                    'sm:px-component-article-card-landscape-image-padding-x',
                    'sm:pr-0'
                ],
                imageWrapper: [
                    'aspect-[1.61/1]'
                ]
            },
            '2': {
                body: [
                    'flex-row',
                    'items-start',
                    'text-left',
                    'h-auto',
                    'md:h-full',
                    'md:justify-between'
                ],
                container: [
                    'h-full',
                    'flex-col',
                    'lg:flex-row'
                ],
                description: [],
                content: [
                    'text-left',
                    'md:flex-1',
                    'md:h-full',
                    'md:min-h-0'
                ],
                contentWrapper: [
                    'text-left',
                    'h-auto',
                    'md:h-full',
                    'justify-start md:justify-between',
                    'md:min-h-0',
                    'py-component-card-item-landscape-content-padding-y',
                    'px-component-card-item-landscape-content-padding-x',
                    'gap-spacing-spacing-24'
                ],
                header: [
                    'aspect-[1.61/1] md:aspect-[16/9] lg:aspect-[5/4] md:min-h-[304px]',
                    'sm:py-component-article-card-landscape-image-padding-y',
                    'sm:px-component-article-card-landscape-image-padding-x',
                    'sm:pr-0',
                    'rounded-r-none md:rounded-b-none lg:rounded-r-none'
                ],
                imageWrapper: [
                    'rounded-r-none',
                    'md:rounded-b-none',
                    'lg:rounded-r-none',
                    'aspect-[1.61/1] md:aspect-[16/9] lg:aspect-[5/4]'
                ]
            },
            '3': {
                header: [
                    'aspect-[1.61/1]'
                ],
                imageWrapper: [],
                container: [
                    'h-full',
                    'flex-col',
                    'md:flex-row',
                    'lg:flex-col'
                ],
                body: [
                    'flex-col',
                    'h-full',
                    'items-start',
                    'text-left'
                ],
                content: [
                    'text-left'
                ],
                contentWrapper: [
                    'h-full',
                    'text-left',
                    'gap-spacing-spacing-24'
                ],
                footer: [
                    'mt-auto'
                ]
            },
            '4': {
                container: [
                    'h-full',
                    'flex-col'
                ],
                body: [
                    'flex-col',
                    'items-center',
                    'text-left'
                ],
                content: [
                    'text-left'
                ],
                contentWrapper: [
                    'h-full',
                    'text-left',
                    'gap-spacing-spacing-24'
                ],
                footer: [
                    'mt-auto'
                ],
                imageWrapper: [
                    'aspect-[1.61/1]'
                ]
            }
        }
    }
});
